import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchBar, Table, TooltipText } from '@/components/common';
import { formatDate } from '@/lib/utils';
import { requestMigrationJobsLoadingSelector } from '@/redux/loading/selectors';
import {
  migrationJobsSelector,
  migrationJobsTotalSelector,
  requestMigrationJobsAsync,
} from '@/modules/AspynMigrator/redux';
import { MigrationReport } from '@/modules/AspynMigrator/components';
import { ViewReport } from './components';

const initialPage = 1;
const initialPageSize = 10;

const statusOptions = [
  { value: '', label: 'Any status' },
  { value: 'queued', label: 'Queued' },
  { value: 'running', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const Jobs = () => {
  const dispatch = useDispatch();
  const jobs = useSelector(migrationJobsSelector);
  const jobsTotal = useSelector(migrationJobsTotalSelector);
  const jobsLoading = useSelector(requestMigrationJobsLoadingSelector);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [selectedPage, setSelectedPage] = useState(initialPage);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  const getJobs = useCallback(() => {
    const params = {
      page: {
        number: selectedPage,
        size: pageSize,
      },
      filter: {},
    };

    if (searchText) {
      params.filter.search = searchText;
    }

    if (statusFilter) {
      params.filter.status = statusFilter;
    }

    dispatch(requestMigrationJobsAsync.request(params));
  }, [dispatch, selectedPage, pageSize, searchText, statusFilter]);

  const handleOpenReport = useCallback((jobId) => {
    const report = jobs.find(({ id }) => id === jobId)?.report ?? null;

    setSelectedReport(report);
    setIsReportModalOpen(!!report);
  }, [jobs]);

  const handleCloseReport = useCallback(() => {
    setIsReportModalOpen(false);

    // Reset the selected report after closing the modal
    const timer = setTimeout(() => setSelectedReport(null), 300);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    getJobs();
  }, [getJobs]);

  const onPageChange = useCallback(({ selected }) => {
    setSelectedPage(selected);
  }, []);

  const onSearchClick = useCallback(({ searchText, searchType }) => {
    setSearchText(searchText);
    setStatusFilter(searchType);
    setSelectedPage(initialPage);
  }, []);

  const getStatusBadge = (status) => {
    const statusColors = {
      queued: 'bg-blue-100 border border-blue-200 text-blue-800',
      running: 'bg-yellow-100 border border-yellow-200 text-yellow-800',
      completed: 'bg-green-100 border border-green-200 text-green-800',
      failed: 'bg-red-100 border border-red-200 text-red-800',
    };

    return (
      <span className={`inline-flex items-center px-1 py-0.5 text-xs font-medium rounded-md ${statusColors[status] || statusColors.queued}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const headRows = [
    { value: 'Job ID' },
    { value: 'Title' },
    { value: 'Status' },
    { value: 'Started At' },
    { value: 'Finished At' },
    { value: 'Report' },
    { value: 'Created At' },
  ];

  const jobRows = jobs.map((job) => ([
    {
      value: (
        <>
          <div>
            <code className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-nowrap rounded-md bg-gray-100 border border-gray-200 text-gray-800">ID: {job.id}</code>
          </div>
          {job.run_id && (
            <div>
              <code className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-nowrap rounded-md bg-gray-100 border border-gray-200 text-gray-800">Run ID: {job.run_id}</code>
            </div>
          )}
          {job.batch_id && (
            <div>
              <code className="inline-flex items-center px-1 py-0.5 text-xs font-medium text-nowrap rounded-md bg-gray-100 border border-gray-200 text-gray-800">Batch ID: {job.batch_id}</code>
            </div>
          )}
        </>
      ),
    },
    { value: job.title },
    { value: getStatusBadge(job.status) },
    {
      value: job.started_at ? (
        <TooltipText
          id={`${job.id}:started_at`}
          text={formatDate(job.started_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.started_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ) : 'n/a',
    },
    {
      value: job.finished_at ? (
        <TooltipText
          id={`${job.id}:finished_at`}
          text={formatDate(job.finished_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.finished_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ) : 'n/a',
    },
    {
      value: job.report ? (
        <ViewReport
          jobId={job.id}
          onClick={handleOpenReport}
        />
      ) : 'n/a',
    },
    {
      value: (
        <TooltipText
          id={`${job.id}:created_at`}
          text={formatDate(job.created_at, formatDate.FORMAT_DISPLAY)}
          message={formatDate(job.created_at, formatDate.FORMAT_TIMESTAMP_DISPLAY)}
        />
      ),
    },
  ]));

  return (
    <div className="flex flex-col w-full h-full p-8">
      <div className="mb-10">
        <h1 className="text-4xl whitespace-nowrap text-gray-900">Migration Jobs</h1>
      </div>

      <div className="flex items-center justify-end gap-4 mb-6">
        <SearchBar
          searchText={searchText}
          searchType={statusFilter}
          typeOptions={statusOptions}
          placeholder="Search by title, job, run or batch ID"
          inputName="search"
          selectName="status"
          disabled={jobsLoading}
          onSearchClick={onSearchClick}
          isSearchTypeSupported={true}
        />
        <button
          type="button"
          onClick={getJobs}
          disabled={jobsLoading}
          className="flex items-center justify-center border border-gray-300 rounded-md shadow-sm px-2 py-2.5 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Refresh jobs"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className="w-5 h-5 text-primary-300"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
        </button>
      </div>

      <div className="grow">
        <Table
          isStickyHeader={true}
          className="relative w-full border-separate border-spacing-0"
          loading={jobsLoading}
          thead={{
            rows: headRows,
          }}
          tbody={{
            rows: jobRows,
          }}
          paginator={{
            pageSize,
            setPageSize,
            onPageChange,
            selectedPage,
            initialPage,
            rowCount: jobsTotal,
          }}
          wrapper={{
            className: 'overflow-y-visible',
          }}
        />
      </div>

      <MigrationReport
        report={selectedReport}
        isOpened={isReportModalOpen}
        onClose={handleCloseReport}
      />
    </div>
  );
};

export default Jobs;
